import { createContext, useContext, useEffect, useState, useCallback } from "react"
import { ethers } from 'ethers';
import { contractInterfaces } from "../utils/contractServices";
import { toast } from "react-toastify";

declare global {
    interface Window {
        ethereum?: any;
    }
}

interface EVMContextType {
    poolContract: ethers.Contract | null;
    lpTokenContract: ethers.Contract | null;
    EVMNetwork: 'mainnet' | 'testnet';
    provider: ethers.BrowserProvider | ethers.JsonRpcProvider | null;
    signer: ethers.Signer | null;
    isWalletLoading: boolean;
    accounts: string[];
    activeAccount?: string;
    setSigner: (signer: ethers.Signer | null) => void;
    setEVMNetwork: (network: 'mainnet' | 'testnet') => void;
    connectEVMWallet: () => Promise<void>;
    disconnectEVMWallet: () => void;
    setActiveAccount?: (account: string) => void;
}

const EVMContext = createContext<EVMContextType | undefined>(undefined);

export const useEVMContext = () => {
    const context = useContext(EVMContext);
    if (context === undefined) {
        throw new Error('useEVMContext must be used within an EVMProvider');
    }
    return context;
}

export const EVMProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [EVMNetwork, setEVMNetwork] = useState<'mainnet' | 'testnet'>('mainnet');
    const [signer, setSigner] = useState<ethers.Signer | null>(null);
    const [accounts, setAccounts] = useState<string[]>([]);
    const [poolContract, setPoolContract] = useState<ethers.Contract | null>(null);
    const [lpTokenContract, setLPTokenContract] = useState<ethers.Contract | null>(null);
    const [isWalletLoading, setIsWalletLoading] = useState(false);
    const [activeAccount, setActiveAccount] = useState<string | undefined>(undefined);

    // Create provider only when window.ethereum is available to avoid
    // invalid EIP-1193 provider errors in browsers without wallet extensions.
    let provider: ethers.BrowserProvider | null = null;
    try {
        if (typeof window !== 'undefined' && window.ethereum) {
            provider = new ethers.BrowserProvider(window.ethereum);
        } else {
            toast.error("Wallet extension is not installed. Please install it to connect your wallet.");
            console.warn('No window.ethereum found — EVM provider not created');
        }
    } catch (err) {
        console.error('Failed to create BrowserProvider:', err);
        provider = null;
    }

    const connectEVMWallet = useCallback(async () => {
        if (!window.ethereum) {
            console.error("MetaMask is not installed");
            toast.error("Wallet extension is not installed. Please install it to connect your wallet.");
            return;
        }
        if (!provider) {
            console.error('EVM provider is not available');
            return;
        }
        setIsWalletLoading(true);
        try {
            setSigner(await provider.getSigner());
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });
            setAccounts(accounts);
        } catch (err) {
            console.error('Failed to connect wallet:', err);
        } finally {
            setIsWalletLoading(false);
        }
    }, [provider]);

    const disconnectEVMWallet = useCallback(() => {
        setSigner(null);
        setAccounts([]);
        setPoolContract(null);
        setLPTokenContract(null);
    }, [provider]);

    useEffect(() => {
        if (signer) {
            setPoolContract(new ethers.Contract(contractInterfaces[EVMNetwork].pool.contractAddress, contractInterfaces[EVMNetwork].pool.poolAbi, signer))
            setLPTokenContract(new ethers.Contract(contractInterfaces[EVMNetwork].lpToken.contractAddress, contractInterfaces[EVMNetwork].lpToken.lpTokenAbi, signer));
            console.log('created contract instances');
        } else {
            setPoolContract(null);
            console.error('No signer found, cannot create contract instance');
        }
    }, [signer, EVMNetwork]);

    const switchCustomNetwork = useCallback(async ({ chainId, url, name }: { chainId: number, url: string, name: string }) => {
        if (window.ethereum) {
            try {
                await window.ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: '0x' + chainId.toString(16) }],
                });
            } catch (switchError) {
                console.error("Error switching network:", switchError);
                // If the chain is not added, add it
                if (
                    typeof switchError === "object" &&
                    switchError !== null &&
                    "code" in switchError &&
                    (switchError as { code?: number }).code === 4902
                ) {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: '0x' + chainId.toString(16),
                            chainName: name,
                            rpcUrls: [url],
                            nativeCurrency: { name: "TAO", symbol: "TAO", decimals: 18 },
                        }],
                    });
                }
            }
        }
    }, []);

    useEffect(() => {
        console.log("Network changed to:", EVMNetwork);
        if (EVMNetwork === "mainnet") {
            switchCustomNetwork({
                chainId: 964,
                url: "https://lite.chain.opentensor.ai",
                name: "Subtensor EVM"
            });
        } else {
            switchCustomNetwork({
                chainId: 945,
                url: "https://test.chain.opentensor.ai",
                name: "Subtensor EVM Testnet"
            });
        }
    }, [switchCustomNetwork, EVMNetwork]);

    const value = {
        poolContract,
        lpTokenContract,
        EVMNetwork,
        provider,
        signer,
        isWalletLoading,
        accounts,
        activeAccount,
        setActiveAccount,
        setSigner,
        setEVMNetwork,
        connectEVMWallet,
        disconnectEVMWallet
    }
    return (
        <EVMContext.Provider value={value}>
            {children}
        </EVMContext.Provider>
    );
};